import { useRouter } from 'next/router';

import { Box, Heading, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftOutline, IconPlusFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import accessUtils from '../../../utils/accessUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import QueryConstants from '../../../constants/QueryConstants';
import { ROUTES } from '../../../constants/urls';

import { EVENT_IDS } from '../../../analytics/events/addPropertyModal';
import { useAuth } from '../../../components/AuthProvider';

import { headingButtonStyles, pageHeadingStyles } from '../styles';

import {
  CLAIM_PROPERTY_FROM_TOP_EVENT_ID,
  emitClaimPropertyFromTopEvent,
} from '../../../analytics/events/claimDashboard';

type Props = {
  handleAddPropertyClick: ({ shouldSendAnalytics }: { shouldSendAnalytics?: boolean }) => void;
  handleAddFeedListingsClick: () => void;
  isArchivedView: boolean;
};

export const PageHeading = ({ handleAddPropertyClick, handleAddFeedListingsClick, isArchivedView }: Props) => {
  const router = useRouter();
  const { user } = useAuth();

  const { data: { unitProperties: claimableProperties = [] } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user?.features?.canClaimFreeListings ?? false,
      },
    );

  const showAddPropertyButton = user?.features?.canAddListing ?? false;
  const showAddFeedListingButton = accessUtils.canAccessAddFeedListingsModal({ user });
  const showClaimPropertiesButton = accessUtils.canAccessClaimPath({
    isScreenMobile: false,
    hasAnyPropertiesToClaim: claimableProperties.length > 0,
    isNonAdvertiser: accessUtils.isNonAdvertiser({ user }),
  });

  const onAddFeedListingClick = () => {
    handleAddFeedListingsClick();
  };

  const onViewAllPropertiesClick = () => {
    router.push({
      pathname: ROUTES.PROPERTIES,
      query: {
        [QueryConstants.SUB_NAV_FILTER_TYPE]: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      },
    });
  };

  return (
    <Box css={pageHeadingStyles}>
      <Box>
        {isArchivedView && (
          <TextButton
            icon={<IconChevronLeftOutline />}
            textStyle="body-bold"
            tone="brand"
            onClick={onViewAllPropertiesClick}
          >
            All properties
          </TextButton>
        )}
        <Heading level={1} css={{ fontSize: 'heading-sm', fontWeight: 'heavy' }}>
          {isArchivedView ? 'Archived Properties' : 'Properties'}
        </Heading>
      </Box>
      <Box css={headingButtonStyles}>
        {showClaimPropertiesButton && (
          <TextButton
            icon={<IconPlusFilled />}
            tone="neutral"
            textStyle="body-sm-bold"
            onClick={() => emitClaimPropertyFromTopEvent({ user })}
            asChild={true}
            data-eventid={CLAIM_PROPERTY_FROM_TOP_EVENT_ID}
          >
            <a href="/rental-manager/ad-management/how-claiming-works">Claim a listing</a>
          </TextButton>
        )}
        {showAddFeedListingButton && (
          <TextButton
            icon={<IconPlusFilled />}
            tone="neutral"
            textStyle="body-sm-bold"
            onClick={onAddFeedListingClick}
            data-eventid={EVENT_IDS.ADD_FEED_CLICK}
          >
            Add a feed
          </TextButton>
        )}
        {showAddPropertyButton && (
          <TextButton
            icon={<IconPlusFilled />}
            tone="neutral"
            textStyle="body-sm-bold"
            onClick={() => handleAddPropertyClick({ shouldSendAnalytics: true })}
            data-eventid={EVENT_IDS.ADD_PROPERTY_CLICK}
          >
            Add a property
          </TextButton>
        )}
      </Box>
    </Box>
  );
};
